<template>
  <b-modal
    ref="modal"
    size="xl"
    centered
    body-class="c-preview"
    @hide="closeHandler"
    @show="showHandler"
  >
    <template #modal-title>
      Preview
      <span
        v-if="fileName"
        class="d-block small text-muted text-wrap text-break font-weight-normal"
      >
        {{ fileName }}
      </span>
    </template>

    <b-tabs
      v-model="activeTab"
      class="preview-tabs"
      :no-nav-style="!hasMetaproperties"
    >
      <!-- Preview Tab -->
      <b-tab
        :title="hasMetaproperties ? 'Preview' : null"
        active
      >
        <div class="c-preview">
          <img
            v-show="!isLoading"
            v-if="imageType"
            class="c-preview__body"
            :src="src"
            alt=""
            @load="loaded"
            @error="error"
          >

          <video
            v-show="!isLoading"
            v-if="videoType"
            class="c-preview__body"
            :src="src"
            controls
            muted
            @loadeddata="loaded"
            @error="error"
          />

          <audio
            v-show="!isLoading"
            v-if="audioType"
            class="c-preview__body"
            :src="src"
            controls
            muted
            @loadeddata="loaded"
            @error="error"
          />

          <iframe
            v-show="!isLoading"
            v-if="pdfType"
            class="c-preview__body"
            :src="src"
            frameborder="0"
            width="100%"
            height="100%"
            @load="loaded"
            @error="error"
          />

          <b-spinner v-if="isLoading" />

          <b-alert
            v-if="isError"
            variant="danger"
            show
          >
            Content is broken or not supported
          </b-alert>
        </div>
      </b-tab>

      <!-- Metaproperties Tab -->
      <b-tab
        v-if="hasMetaproperties"
        title="Properties"
      >
        <div class="metaproperties-content">
          <MetapropertiesTable :metaproperties="payload.metaproperties" />
        </div>
      </b-tab>
    </b-tabs>

    <template #modal-footer>
      <ButtonPrimary
        variant="primary"
        @click="download"
      >
        Download
      </ButtonPrimary>
      <ButtonOutline
        variant="outline-primary"
        @click="$refs.modal.hide()"
      >
        Cancel
      </ButtonOutline>
    </template>
  </b-modal>
</template>

<script>
import ButtonPrimary from "../../../common/atoms/ButtonPrimary.vue";
import ButtonOutline from "../../../common/atoms/ButtonOutline.vue";
import mixinModal from './mixin';
import {AUDIO_FILE_TYPES, IMAGE_FILE_TYPES, VIDEO_FILE_TYPES, PDF_FILE_TYPE, PSD_FILE_TYPE} from "../constants";
import {downloadFileLink, getFileDisplayName} from "../helpers";
import MetapropertiesTable from '@frontend/components/bynder/MetapropertiesTable.vue';
import FileBrowser from "@frontend/services/api/file-browser.js";
import { TABS } from '../constants';

export default {
  name: "PreviewImageModal",
  components: {
    ButtonOutline,
    ButtonPrimary,
    MetapropertiesTable
  },
  mixins: [
    mixinModal
  ],
  props: {
    payload: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    isLoading: false,
    isError: false,
    activeTab: 0,
  }),
  computed: {
    fileName() {
      return getFileDisplayName(this.payload);
    },
    src() {
      if (this.payload.type === 'psd') {
        return `/file/psd/preview?file=${this.payload.original_path}`
      }
      if (this.payload.type === 'eps') {
        return this.payload.thumbnail_path
      }
      return this.payload?.path
    },
    videoType() {
      return !this.isError && VIDEO_FILE_TYPES.includes(this.payload?.type)
    },
    imageType() {
      return !this.isError && IMAGE_FILE_TYPES.includes(this.payload?.type)
    },
    audioType() {
      return !this.isError && AUDIO_FILE_TYPES.includes(this.payload?.type)
    },
    pdfType() {
      return !this.isError && PDF_FILE_TYPE.includes(this.payload?.type)
    },
    psdType() {
      return !this.isError && PSD_FILE_TYPE.includes(this.payload?.type)
    },
    hasMetaproperties() {
      return this.payload &&
        this.payload.metaproperties &&
        Object.keys(this.payload.metaproperties).length > 0;
    }
  },
  methods: {
    async download() {
      try {
        let url = this.payload.path;
        let fileName = this.payload.name;

        const tab = this.$route?.params?.tab;

        if (this.payload?.asset_type === 'bynder') {
          const { data } = await FileBrowser.downloadBynderAssets([this.payload.asset_id]);

          url = data.url;
          fileName = data.fileName;
        } else if (this.payload?.asset_type === 'salsify') {
          const { data } = await FileBrowser.downloadSalsifyAssets([this.payload.id]);

          url = data.url;
          fileName = data.fileName;
        } else if (this.payload?.asset_type === 'box') {
          const { data } = await FileBrowser.importBoxAssets([{
            id: this.payload.id,
            name: this.payload.name,
            extension: this.payload.extension,
            download_url: this.payload.download_url,
          }]);

          url = data?.files?.[0]?.url;
          fileName = this.payload.name;
        } else if (tab === TABS.BACKGROUND || tab === TABS.STOCK_IMAGES) {
          const { data } = await FileBrowser.download({
            tab,
            items: [this.payload],
            file_ids: null,
          });

          url = data.url;
          fileName = data.filename || data.fileName;
        } else if (tab === TABS.PRODUCTS) {
          const { data } = await FileBrowser.download({
            tab,
            items: null,
            file_ids: [this.payload.id],
          });

          url = data.url;
          fileName = data.filename || data.fileName;
        } else {
          // Use the same API as previewFileDownload for regular files
          if (this.payload.id) {
            if (tab === TABS.FILES) {
              const { data } = await FileBrowser.downloadFolderFiles(this.payload.id);
              url = data.url;
              fileName = data.fileName || data.name;
            } else {
              const { data } = await FileBrowser.download([this.payload.id]);
              url = data.url;
              fileName = data.filename || data.name;
            }
          } else {
            url = this.payload.path;
            fileName = this.payload.name;
          }

        }

        downloadFileLink(url, fileName);

      } catch (error) {
        console.log('Error', error.message);
        toastr.error('Download failed. Try again later.');
      }
    },
    showHandler() {
      const epsPreviewUnavailable = this.payload?.type === 'eps' && !this.payload.thumbnail_path;

      this.isLoading = !epsPreviewUnavailable;
      this.isError = epsPreviewUnavailable;
      this.activeTab = 0;
    },
    loaded() {
      this.isLoading = false;
    },
    error() {
      this.isError = true;
      this.isLoading = false;
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep .c-preview {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  &__body {
    max-width: 100%;
    max-height: 100%;
  }
}

.preview-tabs {
  ::v-deep .nav-tabs {
    border-bottom: none;
    margin-bottom: 20px;
    justify-content: flex-start !important;
    display: flex !important;
    padding-left: 2rem;

    a.nav-link {
      font-weight: 700;
      color: #727272;
      margin-left: 0 !important;
    }

    a.nav-link:hover {
      border-radius: 12px;
      border-color: #007964;
    }

    a.nav-link.active {
      padding: 6px 23px;
      border-radius: 12px;
      background-color: #007964;
      color: #f6f6f6;
      pointer-events: none;
    }

    .nav-item {
      margin-left: 0 !important;
      margin-right: 0.5rem !important;
    }
  }

  ::v-deep .tab-content {
    height: 70vh;
    overflow: hidden;
  }

  ::v-deep .tab-pane {
    height: 100%;
  }
}

.metaproperties-content {
  height: 100%;
  overflow-y: auto;
  padding: 1rem 0;
}
</style>
